import http from 'hub-http/clients/apiClient';
import { Map as ImmutableMap, Set as ImmutableSet } from 'immutable';
import invariant from 'react-utils/invariant';
import { GATES, OBJECT_TYPE_TO_ENDPOINT_MAPPINGS, RETINA_MULTIPLIER } from '../Constants';
import { AVATAR_SOURCES } from '../lib/AvatarSources';
import { getUnauthedHubSpotUserAvatarUrl } from '../lib/getUnauthedHubSpotUserAvatarUrl';
import { isUngatedFor } from './gates';
import { DEFAULT_CACHE_ENTRY } from '../constants/cacheConstants';
export const BASE_PATH = 'avatars/v1/';
export const BATCH_GET_PATH = 'avatars/batch';
export const UPLOAD_PATH = 'overrides';
/**
 * Reduces the original lookups and the response into a Map of lookup => url.
 */
export function normalizeUpdates(lookups, response) {
  return lookups.reduce((updates, lookup) => {
    var _response$identifierN;
    const identifierName = lookup.getIdentifierName();
    const identifier = lookup.getIdentifier();
    if (!identifierName || !identifier) {
      return updates.set(lookup, DEFAULT_CACHE_ENTRY);
    }
    const responseData = (_response$identifierN = response[identifierName]) === null || _response$identifierN === void 0 ? void 0 : _response$identifierN[String(identifier)];
    if (responseData !== null && responseData !== void 0 && responseData.queued) {
      return updates;
    }
    return updates.set(lookup, {
      uri: (responseData === null || responseData === void 0 ? void 0 : responseData.uri) || null,
      source: (responseData === null || responseData === void 0 ? void 0 : responseData.source) || null
    });
  }, ImmutableMap());
}

/**
 * Reduces the original lookups and the response into a Set of lookups that are
 * queued and should be retried.
 */
export function normalizeRetries(lookups, response) {
  return lookups.reduce((retries, lookup) => {
    var _response$identifierN2;
    const identifierName = lookup.getIdentifierName();
    const identifier = lookup.getIdentifier();
    if (!identifierName || !identifier) {
      return retries;
    }
    const responseData = (_response$identifierN2 = response[identifierName]) === null || _response$identifierN2 === void 0 ? void 0 : _response$identifierN2[String(identifier)];
    if (!(responseData !== null && responseData !== void 0 && responseData.queued)) {
      return retries;
    }
    return retries.add(lookup);
  }, ImmutableSet());
}
export function normalizeError(lookups, response) {
  return {
    retry: ImmutableSet(),
    updates: normalizeUpdates(lookups, response)
  };
}
export function normalizeResponse(lookups, response) {
  return {
    retry: normalizeRetries(lookups, response),
    updates: normalizeUpdates(lookups, response)
  };
}

/**
 * Takes a list of lookups in transforms them to a JSON structure that the api
 * understands.
 */
export function toRequestData(lookups, dimension) {
  invariant(ImmutableSet.isSet(lookups), 'expected `lookups` to be a Set but got `%s`', lookups);
  invariant(typeof dimension === 'undefined' || typeof dimension === 'number', 'expected `dimension` to be undefined or a number but got `%s`', lookups);
  const requestDataWithoutDimensions = lookups.reduce((builder, lookup) => {
    const identifierName = lookup.getIdentifierName();
    if (!identifierName) {
      return builder;
    }
    const withoutKnown = builder.update(identifierName, maybeSet => {
      const identifier = lookup.getIdentifier();
      return typeof maybeSet === 'undefined' ? ImmutableSet.of(identifier) : maybeSet.add(identifier);
    });
    if (!lookup.isKnown()) {
      return withoutKnown;
    }
    const knownName = lookup.getKnownName();
    if (!knownName) {
      return withoutKnown;
    }
    return withoutKnown.update(knownName, maybeSet => {
      const knownObject = lookup.getKnownObject();
      return typeof maybeSet === 'undefined' ? ImmutableSet.of(knownObject) : maybeSet.add(knownObject);
    });
  }, ImmutableMap());
  if (typeof dimension === 'number') {
    const doubleDimensionForRetina = dimension * RETINA_MULTIPLIER;
    const sizeParams = {
      width: doubleDimensionForRetina,
      height: doubleDimensionForRetina
    };
    return Object.assign({}, requestDataWithoutDimensions.toJS(), {
      sizeParams
    });
  }
  return requestDataWithoutDimensions.toJS();
}
export function getUnauthedAvatarData(requestData) {
  const isUngatedForTrellisIcons = isUngatedFor(GATES.TRELLIS_AVATAR_ICONS_GATE);
  if (!isUngatedForTrellisIcons || !('hubSpotUserEmails' in requestData) || requestData.hubSpotUserEmails.length === 0) return {
    unauthedData: {},
    hasRemainingLookups: true
  };
  const unauthedData = requestData.hubSpotUserEmails.reduce((acc, email) => {
    var _requestData$sizePara;
    acc.hubSpotUserEmails[email] = {
      fromDefault: false,
      queued: false,
      source: AVATAR_SOURCES.HUBSETTINGS_HUBSPOT_USER,
      uri: getUnauthedHubSpotUserAvatarUrl({
        hubSpotUserEmail: email,
        dimension: (_requestData$sizePara = requestData.sizeParams) === null || _requestData$sizePara === void 0 ? void 0 : _requestData$sizePara.width,
        isUngatedForTrellisIcons: true
      })
    };
    return acc;
  }, {
    hubSpotUserEmails: {}
  });
  delete requestData.hubSpotUserEmails;
  const hasRemainingLookups = Object.keys(requestData).filter(key => key !== 'sizeParams').length > 0;
  return {
    unauthedData,
    hasRemainingLookups
  };
}

/**
 * Uses `fetcher` to retrieve and then normalize responses from the API.
 */
export function fetchAvatars(fetcher, portalId, lookups, dimension) {
  invariant(typeof fetcher === 'function', 'expected `fetcher` to be a function but got `%s`', fetcher);
  invariant(typeof portalId === 'number' || typeof portalId === 'undefined', 'expected `portalId` to be a number or undefined but got `%s`', portalId);
  invariant(ImmutableSet.isSet(lookups), 'expected `lookups` to be a Set but got `%s`', lookups);
  invariant(typeof dimension === 'number' || typeof dimension === 'undefined', 'expected `dimension` to be a number or undefined but got `%s`', dimension);
  const portalIdQueryString = typeof portalId !== 'undefined' ? `?portalId=${portalId}` : '';
  const requestData = toRequestData(lookups, dimension);
  const {
    unauthedData,
    hasRemainingLookups
  } = getUnauthedAvatarData(requestData);
  if (!hasRemainingLookups) {
    return Promise.resolve(normalizeResponse(lookups, unauthedData));
  }
  return fetcher(`${BASE_PATH}${BATCH_GET_PATH}${portalIdQueryString}`, requestData).then(response => {
    if (typeof response !== 'object' || response === null) {
      throw new Error('Invalid response format');
    }
    return normalizeResponse(lookups, Object.assign({}, response, unauthedData));
  }).catch(() => {
    // If the request fails, we return an empty responses
    // for all lookups which results in the use of the fallback avatar icon or image
    return normalizeError(lookups, unauthedData);
  });
}

/**
 * Uses `uploader` to submit an avatar override
 */
export function uploadAvatar(portalId, objectType, objectId, formData) {
  invariant(typeof portalId === 'number', 'expected `portalId` to be a number but got `%s`', portalId);
  const acceptableTypes = Object.keys(OBJECT_TYPE_TO_ENDPOINT_MAPPINGS);
  invariant(acceptableTypes.indexOf(objectType) >= 0, `expected \`objectType\` to be one of ${acceptableTypes} but got \`%s\``, objectType);
  invariant(typeof objectId === 'number', 'expected `objectId` to be a number but got `%s`', objectId);
  invariant(formData instanceof FormData, 'expected `formData` to be an instance of FormData but got `%s`', formData);
  const endpoint = OBJECT_TYPE_TO_ENDPOINT_MAPPINGS[objectType];
  return http.post(`${BASE_PATH}${UPLOAD_PATH}/${endpoint}/${objectId}?portalId=${portalId}`, {
    data: formData,
    timeout: 60000,
    headers: {
      'content-type': false,
      'process-data': false
    }
  });
}